import { Recapt } from '../engine';
import { RequestManager } from '../managers/requestManager';
import { BaseEvent, BaseModule } from './base';
import { TRACKING_URL_PATTERNS } from './performance';
import { Namespace } from './types';

interface NavigationEvent extends BaseEvent {
  to: string;
  from: string;
}

/**
 * Quiet period (ms) for network idle detection.
 * After the last fetch/XHR resource entry completes, wait this long
 * before considering the network idle and triggering a FullSnapshot.
 */
const NETWORK_QUIET_PERIOD_MS = 750;

/**
 * Maximum wait (ms) after SPA navigation before forcing a FullSnapshot.
 * Prevents infinite waiting when the page has long-polling, streaming,
 * or other persistent connections.
 */
const MAX_NETWORK_WAIT_MS = 8000;

/**
 * Minimum wait (ms) after navigation before a network-idle snapshot
 * can fire. Gives the framework time to initiate its API calls so we
 * don't snap before the first request even goes out.
 */
const MIN_WAIT_AFTER_NAV_MS = 300;

/**
 * Returns true if the URL belongs to a known tracking / analytics service
 * that should be ignored when determining network idle state.
 */
function isTrackingRequest(url: string): boolean {
  return TRACKING_URL_PATTERNS.some((p) => url.includes(p));
}

export class NavigationModule extends BaseModule<NavigationEvent> {
  public namespace: Namespace = 'navigations';
  private currentPath: string = '';
  private isPolling = false;
  private timeout: NodeJS.Timeout | null = null;

  /** Cleanup function for the current network-idle detection session. */
  private networkIdleCleanup: (() => void) | null = null;

  timeoutPoll() {
    const that = this;
    this.timeout = setTimeout(() => {
      that.poll();
    }, 200);
  }

  poll() {
    if (!this.isPolling) return;

    const path = window.location.pathname;

    if (path === this.currentPath) {
      this.timeoutPoll();
      return;
    }

    const evt = {
      timestamp: Recapt.getCurrentSessionTimestamp(),
      where: Recapt.where(),
      to: path,
      from: this.currentPath,
    };

    if (evt.from.length === 0) {
      evt.from = evt.to;
    }

    RequestManager.potentialPainpoint();

    this.state.push(evt);

    this.currentPath = path;

    this.schedulePostNavSnapshot();

    this.timeoutPoll();
  }

  /**
   * After SPA navigation, detect when the page has settled by watching
   * fetch/XHR resource completions via PerformanceObserver.
   *
   * Triggers a FullSnapshot once no new fetch/XHR resources have completed
   * for NETWORK_QUIET_PERIOD_MS, or after MAX_NETWORK_WAIT_MS at the latest.
   *
   * Falls back to a fixed 2 s delay if PerformanceObserver is unavailable.
   */
  private schedulePostNavSnapshot() {
    this.cancelPendingDetection();

    try {
      if (
        typeof PerformanceObserver === 'undefined' ||
        !PerformanceObserver.supportedEntryTypes?.includes('resource')
      ) {
        this.fallbackFixedDelay();
        return;
      }

      this.startNetworkIdleDetection();
    } catch {
      this.fallbackFixedDelay();
    }
  }

  /** Fixed-delay fallback when PerformanceObserver is not available. */
  private fallbackFixedDelay() {
    const timer = setTimeout(() => {
      Recapt.takeFullSnapshot();
    }, 2000);
    this.networkIdleCleanup = () => clearTimeout(timer);
  }

  /**
   * Observes fetch/XHR resource timing entries. Each time one completes
   * the quiet timer resets. When nothing has completed for
   * NETWORK_QUIET_PERIOD_MS the page is considered settled and a
   * FullSnapshot is taken.
   */
  private startNetworkIdleDetection() {
    const navTime = Date.now();
    let quietTimer: NodeJS.Timeout | null = null;
    let maxTimer: NodeJS.Timeout | null = null;
    let settled = false;

    const doSnapshot = () => {
      if (settled) return;
      settled = true;
      cleanup();
      Recapt.takeFullSnapshot();
    };

    const resetQuietTimer = () => {
      if (settled) return;
      if (quietTimer) clearTimeout(quietTimer);

      quietTimer = setTimeout(() => {
        // Make sure we've waited at least MIN_WAIT_AFTER_NAV_MS since nav
        const elapsed = Date.now() - navTime;
        if (elapsed < MIN_WAIT_AFTER_NAV_MS) {
          setTimeout(doSnapshot, MIN_WAIT_AFTER_NAV_MS - elapsed);
        } else {
          doSnapshot();
        }
      }, NETWORK_QUIET_PERIOD_MS);
    };

    let observer: PerformanceObserver | null = null;

    try {
      observer = new PerformanceObserver((list) => {
        if (settled) return;
        try {
          const entries = list.getEntries();
          for (const entry of entries) {
            const res = entry as PerformanceResourceTiming;

            if (
              res.initiatorType !== 'fetch' &&
              res.initiatorType !== 'xmlhttprequest'
            ) {
              continue;
            }

            // Ignore tracking/analytics pings — they shouldn't delay snapshots
            try {
              if (isTrackingRequest(res.name)) continue;
            } catch {
              /* malformed URL — treat as real request */
            }

            // A real API request just completed — reset the quiet timer
            resetQuietTimer();
            return;
          }
        } catch {
          /* never throw on customer site */
        }
      });

      observer.observe({ type: 'resource', buffered: false });
    } catch {
      // Observer failed — fall back
      this.fallbackFixedDelay();
      return;
    }

    // Start the quiet timer immediately.
    // If the page makes no API calls at all, the snapshot fires after
    // NETWORK_QUIET_PERIOD_MS (+ MIN_WAIT_AFTER_NAV_MS if needed).
    resetQuietTimer();

    // Absolute max wait — never hang forever
    maxTimer = setTimeout(doSnapshot, MAX_NETWORK_WAIT_MS);

    const cleanup = () => {
      try {
        observer?.disconnect();
      } catch {
        /* */
      }
      if (quietTimer) clearTimeout(quietTimer);
      if (maxTimer) clearTimeout(maxTimer);
    };

    this.networkIdleCleanup = cleanup;
  }

  /** Tears down any in-progress network idle detection. */
  private cancelPendingDetection() {
    if (this.networkIdleCleanup) {
      this.networkIdleCleanup();
      this.networkIdleCleanup = null;
    }
  }

  onStart() {
    const stored = this.loadStore<{
      state: NavigationEvent[];
      currentPath: string;
    }>();

    if (stored) {
      this.state = stored.state;
      if (stored.currentPath) this.currentPath = stored.currentPath;
    }

    this.store(() => ({
      state: this.state,
      currentPath: this.currentPath,
    }));

    this.isPolling = true;
    this.poll();
  }

  onResume() {
    this.isPolling = true;
    this.poll();
  }

  onStop() {
    this.isPolling = false;
    if (this.timeout) {
      clearTimeout(this.timeout);
    }
    this.cancelPendingDetection();
  }

  purgeState(): void {
    this.state = [];
  }

  buildRequest(
    state: NavigationEvent[],
  ): Record<string, unknown> & { state: NavigationEvent[] } {
    return {
      state,
    };
  }
}
